import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchStudent, addShoppingCartItems } from "../../../../api/action/user";
import { Snackbar } from "../../../../components/ui/Feedback";
import InputField from "../components/CustomFields/InputField";
import Dropdown from "../components/CustomFields/Dropdown";
import { KRUMAGO } from "../../../s3-assets/Krumago";

const ApplicantForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userDetail = useSelector((state) => state?.auth?.data);
    const companyDetail = useSelector((state) => state.companyReducer.company);

    const [student, setStudent] = useState({
        primaryId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        gender: '',
        status: 'NEW',
        addressList: [{ streetName1: '', streetName2: '', city: '', state: '', zipcode: '', country: '', type: 'HOME' }],
        educationDetails: [{ schoolName: '', degree: '', currentlyStudying: false, startDate: '', endDate: '' }],
        dynamicAttributes: [
            { name: 'Nationality', value: '' },
            { name: 'Community', value: '' },
            { name: 'Language', value: '' },
            { name: 'Stream', value: '' },
            { name: 'Registration Class', value: '' },
            { name: 'How did you know about Velammal', value: '' },
        ],
        company: { primaryId: 'companyId', code: 'companyId' },
        userRole: 'APPLICANT',
        contactPerson: { firstName: '', lastName: '', email: '', phoneNumber: '', dynamicAttributes: [{ name: 'Relationship', value: "Mother" }] },
        parentDetails: [{ firstName: '', lastName: '', email: '', phoneNumber: '', occupation: '', annualIncome: '', dynamicAttributes: [{ name: 'Relationship', value: 'Mother' }] }]
    });

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('info');
    const [registrationClass, setRegistrationClass] = useState('');

    useEffect(() => {
        const loadStudentData = async () => {
            try {
                const email = localStorage.getItem('email');

                if (!email || typeof email !== 'string') {
                    setSnackbarMessage('No valid email found in localStorage');
                    setSnackbarSeverity('error');
                    setOpenSnackbar(true);
                    return;
                }

                const response = await dispatch(fetchStudent({ email }));

                if (response.error) {
                    setSnackbarMessage('Failed to fetch student details');
                    setSnackbarSeverity('error');
                    setOpenSnackbar(true);
                } else {
                    setStudent({
                        firstName: response.firstName || '',
                        primaryId: response.primaryId || '',
                        lastName: response.lastName || '',
                        email: response.email || '',
                        phoneNumber: response.phoneNumber || '',
                        gender: response.gender || '',
                        addressList: response?.addressList || [{ streetName1: '', city: '', state: '', zipcode: '', country: '' }],
                        educationDetails: response?.educationDetails || [{ schoolName: '', degree: '' }],
                        parentDetails: response?.parentDetails || [{ firstName: '', lastName: '', email: '', phoneNumber: '', occupation: '', annualIncome: '' }],
                    });
                }
            } catch (error) {
                setSnackbarMessage('An error occurred while fetching student details');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        };

        loadStudentData();
        const storedRegistrationClass = localStorage.getItem('selectedRegistrationClass');
        if (storedRegistrationClass) {
            setRegistrationClass(JSON.parse(storedRegistrationClass));
        }
    }, [dispatch]);

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleChange = (field, value) => {
        setStudent((prevStudent) => ({
            ...prevStudent,
            [field]: value,
        }));
    };

    const handleChangeDynamicAttribute = (idx, value) => {
        const updatedAttributes = [...student.dynamicAttributes];
        updatedAttributes[idx].value = value;
        setStudent((prevStudent) => ({
            ...prevStudent,
            dynamicAttributes: updatedAttributes
        }));
    };

    const addToShoppingCart = async (product) => {
        const shoppingCartId = userDetail.account.shoppingCart.primaryId;
        if (!shoppingCartId) {
            return;
        }

        try {
            const response = await addShoppingCartItems(shoppingCartId, product);
            if (response) {
                navigate("/cart");
            }
        } catch (error) {
            setSnackbarMessage('An error occurred while adding to the cart');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleCart = async () => {
        const localStorageData = JSON.parse(localStorage.getItem("selectedRegistrationClass"));
        const company = companyDetail;

        let scheduledReq = {
            primaryId: localStorageData.primaryId || null,
            createdDate: localStorageData.createdDate || null,
            createdBy: localStorageData.createdBy || null,
            modifiedDate: localStorageData.modifiedDate || null,
            modifiedBy: localStorageData.modifiedBy || null,
            dynamicAttributes: localStorageData.dynamicAttributes || null,
            deleted: localStorageData.deleted || false,
            quantity: 1,
            company: company || {},
            totalPrice: localStorageData.product?.price?.priceAmt || 0,
            lineitemHistory: localStorageData.lineitemHistory || null,
            product: localStorageData.product || null,
            scheduledProduct: {
                primaryId: localStorageData.primaryId,
                createdDate: localStorageData.createdDate,
                createdBy: localStorageData.createdBy,
                modifiedDate: localStorageData.modifiedDate,
                modifiedBy: localStorageData.modifiedBy,
                dynamicAttributes: localStorageData.dynamicAttributes,
                deleted: localStorageData.deleted || false,
                period: {
                    effectiveStartDate: localStorageData.period?.effectiveStartDate || null,
                    effectiveEndDate: localStorageData.period?.effectiveEndDate || null,
                    daysOfSchedule: localStorageData.period?.daysOfSchedule || [],
                    durationInWeeks: localStorageData.period?.durationInWeeks || 0,
                    durationInDays: localStorageData.period?.durationInDays || 0,
                    durationType: localStorageData.period?.durationType || null,
                    duration: localStorageData.period?.duration || 0
                },
                owners: localStorageData.owners || [],
                product: {
                    primaryId: localStorageData.product?.primaryId || null,
                    _id: localStorageData.product?._id || null,
                    createdDate: localStorageData.product?.createdDate || null,
                    createdBy: localStorageData.product?.createdBy || null,
                    modifiedDate: localStorageData.product?.modifiedDate || null,
                    modifiedBy: localStorageData.product?.modifiedBy || null,
                    deleted: localStorageData.product?.deleted || false,
                    name: localStorageData.product?.name || '',
                    description: localStorageData.product?.description || '',
                    code: localStorageData.product?.code || '',
                    deliveryType: localStorageData.product?.deliveryType || 'UNKNOWN',
                    category: localStorageData.product?.category || {},
                    inStock: localStorageData.product?.inStock || false,
                    imageUrlsList: localStorageData.product?.imageUrlsList || [],
                    attributes: localStorageData.product?.attributes || {},
                    price: localStorageData.product?.price || {}
                },
                schedule: {
                    primaryId: localStorageData.schedule?.primaryId || null,
                    _id: localStorageData.schedule?._id || null,
                    createdDate: localStorageData.schedule?.createdDate || null,
                    createdBy: localStorageData.schedule?.createdBy || null,
                    deleted: localStorageData.schedule?.deleted || false,
                    title: localStorageData.schedule?.title || '',
                    daysOfSchedule: localStorageData.schedule?.daysOfSchedule || [],
                    startTime: localStorageData.schedule?.startTime || '00:00:00',
                    endTime: localStorageData.schedule?.endTime || '00:00:00',
                    scheduleCode: localStorageData.schedule?.scheduleCode || '',
                    totalHours: localStorageData.schedule?.totalHours || 0
                }
            },
            status: localStorageData.status || 'PENDING'
        };

        await addToShoppingCart(scheduledReq);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const productDetails = JSON.parse(localStorage.getItem("selectedRegistrationClass"));
        handleCart(productDetails);
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center bg-cover bg-center mb-6"
            style={{ backgroundImage: `url(${KRUMAGO.IMAGES.ADMISSION})` }}
        >
            <div className="max-w-5xl w-full">
                <br /> <br />
                <div className="bg-white bg-opacity-70 p-6 rounded-lg shadow-md max-w-5xl w-full">
                    <div className="bg-[#717d58] p-2 rounded shadow-md max-w-xl mx-auto">
                        <h1 className="text-2xl font-bold text-center text-white mb-3">ONLINE APPLICATION REGISTRATION</h1>
                    </div>
                    <br />
                    <div className="flex justify-center space-x-4">
                        {registrationClass && (
                            <div className="bg-[#65A262E6] p-4 rounded-lg mb-4 w-80 flex items-center justify-center">
                                <h2 className="text-lg font-semibold text-white text-center">
                                    Selected Registration Class {registrationClass?.product?.name} - STD
                                </h2>
                            </div>
                        )}
                        <div className="bg-[#65A262E6] p-4 rounded-lg mb-4 w-80 flex items-center justify-center">
                            <h2 className="text-lg font-semibold text-white text-center">
                                Reference Id: {student?.primaryId}
                            </h2>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <section className="space-y-6 bg-gray-100 p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-semibold text-[#824296] text-center">Personal Information</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                {/* First Name */}
                                <InputField
                                    id="firstName"
                                    label="First Name *"
                                    value={student.firstName}
                                    onChange={(e) => handleChange('firstName', e.target.value)}
                                //placeholder="Enter your first name"
                                />

                                {/* Last Name */}
                                <InputField
                                    id="lastName"
                                    label="Last Name *"
                                    value={student.lastName}
                                    onChange={(e) => handleChange('lastName', e.target.value)}
                                //placeholder="Enter your last name"
                                />

                                {/* Email */}
                                <InputField
                                    id="email"
                                    label="Email *"
                                    value={student.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    //placeholder="Enter your email"
                                    type="email"
                                />

                                {/* Phone Number */}
                                <InputField
                                    id="phoneNumber"
                                    label="Phone Number *"
                                    value={student.phoneNumber}
                                    onChange={(e) => handleChange('phoneNumber', e.target.value)}
                                //placeholder="Enter your phone number"
                                />

                                {/* Gender */}
                                <Dropdown
                                    id="gender"
                                    label="Gender *"
                                    value={student.gender}
                                    onChange={(e) => handleChange('gender', e.target.value)}
                                    options={["MALE", "FEMALE"]}
                                    defaultOption="Select Gender *"
                                />

                                {/* Dynamic Attributes */}
                                {student.dynamicAttributes?.map((attribute, index) => (
                                    <InputField
                                        key={index}
                                        id={`dynamicAttribute-${index}`}
                                        label={attribute.name}
                                        value={attribute.value}
                                        onChange={(e) => handleChangeDynamicAttribute(index, e.target.value)}
                                    //placeholder={`Enter ${attribute.name}`}
                                    />
                                ))}
                            </div>
                        </section>

                        {/* Education Details Section */}
                        <section className="space-y-6 bg-gray-100 p-6 rounded-lg shadow-md mt-6">
                            <h2 className="text-lg font-semibold text-[#824296] text-center">Education Details</h2>
                            {student.educationDetails?.map((edu, idx) => (
                                <div key={idx} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* School Name */}
                                        <InputField
                                            id={`schoolName-${idx}`}
                                            label="School Name *"
                                            value={edu.schoolName}
                                            onChange={(e) => handleChange(`educationDetails[${idx}].schoolName`, e.target.value)}
                                        //placeholder="Enter school name"
                                        />

                                        {/* Degree */}
                                        <InputField
                                            id={`degree-${idx}`}
                                            label="Degree *"
                                            value={edu.degree}
                                            onChange={(e) => handleChange(`educationDetails[${idx}].degree`, e.target.value)}
                                        //placeholder="Enter degree"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* Start Date */}
                                        <InputField
                                            id={`startDate-${idx}`}
                                            label="Start Date *"
                                            value={edu.startDate}
                                            onChange={(e) => handleChange(`educationDetails[${idx}].startDate`, e.target.value)}
                                            //placeholder="Select start date"
                                            type="date"
                                        />

                                        {/* End Date */}
                                        <InputField
                                            id={`endDate-${idx}`}
                                            label="End Date *"
                                            value={edu.endDate}
                                            onChange={(e) => handleChange(`educationDetails[${idx}].endDate`, e.target.value)}
                                            //placeholder="Select end date"
                                            type="date"
                                        />
                                    </div>
                                </div>
                            ))}
                        </section>

                        {/* Parent Details Section */}
                        <section className="space-y-6 bg-gray-100 p-6 rounded-lg shadow-md mt-6">
                            <h2 className="text-lg font-semibold text-[#824296] text-center">Parent Details</h2>
                            {student.parentDetails?.map((parent, idx) => (
                                <div key={idx} className="space-y-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* First Name */}
                                        <InputField
                                            id={`parentFirstName-${idx}`}
                                            label="First Name *"
                                            value={parent.firstName}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].firstName`, e.target.value)}
                                        //placeholder="Enter first name"
                                        />

                                        {/* Last Name */}
                                        <InputField
                                            id={`parentLastName-${idx}`}
                                            label="Last Name *"
                                            value={parent.lastName}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].lastName`, e.target.value)}
                                        //placeholder="Enter last name"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* Email */}
                                        <InputField
                                            id={`parentEmail-${idx}`}
                                            label="Email *"
                                            value={parent.email}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].email`, e.target.value)}
                                            //placeholder="Enter email"
                                            type="email"
                                        />

                                        {/* Phone Number */}
                                        <InputField
                                            id={`parentPhoneNumber-${idx}`}
                                            label="Phone Number *"
                                            value={parent.phoneNumber}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].phoneNumber`, e.target.value)}
                                        //placeholder="Enter phone number"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {/* Occupation */}
                                        <InputField
                                            id={`parentOccupation-${idx}`}
                                            label="Occupation *"
                                            value={parent.occupation}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].occupation`, e.target.value)}
                                        //placeholder="Enter occupation"
                                        />

                                        {/* Annual Income */}
                                        <InputField
                                            id={`parentIncome-${idx}`}
                                            label="Annual Income *"
                                            value={parent.annualIncome}
                                            onChange={(e) => handleChange(`parentDetails[${idx}].annualIncome`, e.target.value)}
                                        //placeholder="Enter annual income"
                                        />
                                    </div>
                                </div>
                            ))}
                        </section>

                        {/* Submit Button */}
                        <div className="flex justify-center mt-8">
                            <button type="submit" className="bg-[#824296] text-white p-3 rounded-md">Submit & pay</button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Snackbar for error handling */}
            <Snackbar
                autoHideDuration={6000}
                isOpen={openSnackbar}
                message={snackbarMessage}
                onClose={handleCloseSnackbar}
                tone={snackbarSeverity}
            />
        </div>
    );
};

export default ApplicantForm;
